import qs from 'qs';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { getValidToken, dispatchSessionExpired } from '../utils/tokenRefresh';

const errorCodes: { [key: number]: string } = {
  400: "Something went wrong with your request. Please check your input and try again.",
  401: "You need to log in to access this feature. Please authenticate and retry.",
  403: "Access denied. You do not have permission to perform this action.",
  404: "We couldn’t find what you were looking for. Please try again.",
  409: "A conflict occurred. Please refresh the page or try again later.",
  500: 'Oops! Something went wrong on our side. We’re working to fix this.',
  502: 'Service temporarily overloaded.',
  503: 'The service is currently unavailable. Please try again after some time',
  504: 'The request took too long to process. Please try again later.',
  507: "Error occurred while making Cosmos DB connection or while fetching the Cosmos data.",
  508: "Error occurred while making PostgreSQL DB connection or while fetching the PostgreSQL data.",
};

class WizrAPIHandler {
  private _instance: AxiosInstance;

  constructor() {
    this._instance = axios.create({ timeout: 600000 });
    this._instance.interceptors.request.use(this.requestInterceptor, this.errorHandler);
    this._instance.interceptors.response.use(this.responseInterceptor, this.errorHandler);
  }

  get(url: string) {
    return this._instance.get(url);
  }

  post(url: string, params: { [key: string]: string | number }) {
    return this._instance.post(url, params);
  }

  delete(url: string) {
    return this._instance.delete(url);
  }

  put(url: string, params: { [key: string]: string | number }) {
    return this._instance.put(url, params);
  }

  appTestPost(url: string, params: { [key: string]: string | number }) {
    const appTestInstance = axios.create({
      baseURL: this._instance.defaults.baseURL,
      timeout: this._instance.defaults.timeout,
    });
    appTestInstance.interceptors.request.use(this.formpostRequestInterceptor, this.errorHandler);
    appTestInstance.interceptors.response.use(this.responseInterceptor, this.errorHandler);

    appTestInstance.defaults.headers['Content-Type'] = 'application/json';
    appTestInstance.defaults.headers['wizr-auth'] = localStorage.getItem('appshare_token');

    return appTestInstance.post(url, params);
  }

  controlRoomApis(url: string, params: { [key: string]: string | number }) {
    const controlroomapiInstance = axios.create({
      baseURL: this._instance.defaults.baseURL,
      timeout: this._instance.defaults.timeout,
    });
    controlroomapiInstance.interceptors.request.use(this.formpostRequestInterceptor, this.errorHandler);
    controlroomapiInstance.interceptors.response.use(this.responseInterceptor, this.errorHandler);

    controlroomapiInstance.defaults.headers['Content-Type'] = 'application/json';
    controlroomapiInstance.defaults.headers['Authorization'] = 'Basic M2RiZmFmYjFlZjE0NDg5MjllZjRjOTY4YjFkYzZlYTA6TGcxL1A1KzcrNE9Ya0VwS0JhMzdmR2J3YkI0alp5eXlCa1dLK3lYcHJhRT0=';

    return controlroomapiInstance.post(url, params);
  }

  async formDatapost(url: string, params: { [key: string]: string | number }, customHeaders?: { [key: string]: string }) {
    const formpostInstance = axios.create({
      baseURL: this._instance.defaults.baseURL,
      timeout: this._instance.defaults.timeout,
    });
    formpostInstance.interceptors.request.use(this.formpostRequestInterceptor, this.errorHandler);
    formpostInstance.interceptors.response.use(this.responseInterceptor, this.errorHandler);

    const apiKey = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY;
    const apiKeyValue = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY_VALUE;
    if (apiKey && apiKeyValue) {
      formpostInstance.defaults.headers[apiKey] = apiKeyValue;
    } else {
      console.error('API key value is undefined');
    }

    formpostInstance.defaults.headers['Content-Type'] = 'multipart/form-data';
    formpostInstance.defaults.headers['wizr-auth'] = await getValidToken() ?? localStorage.getItem('id_token');

    // Add custom headers if provided
    if (customHeaders) {
      Object.keys(customHeaders).forEach(key => {
        formpostInstance.defaults.headers[key] = customHeaders[key];
      });
    }

    return formpostInstance.post(url, params);
  }

  fileUploadInAppShare(url: string, params: { [key: string]: string | number }) {
    const appShareInstance = axios.create({
      baseURL: this._instance.defaults.baseURL,
      timeout: this._instance.defaults.timeout,
    });
    appShareInstance.interceptors.request.use(this.formpostRequestInterceptor, this.errorHandler);
    appShareInstance.interceptors.response.use(this.responseInterceptor, this.errorHandler);

    appShareInstance.defaults.headers['Content-Type'] = 'multipart/form-data';
    appShareInstance.defaults.headers['wizr-auth'] = localStorage.getItem('appshare_token');

    return appShareInstance.post(url, params);
  }

  appshareget(url: string) {
    const appShareInstance = axios.create({
      baseURL: this._instance.defaults.baseURL,
      timeout: this._instance.defaults.timeout,
    });
    appShareInstance.interceptors.request.use(this.formpostRequestInterceptor, this.errorHandler);
    appShareInstance.interceptors.response.use(this.responseInterceptor, this.errorHandler);

    appShareInstance.defaults.headers['Content-Type'] = 'multipart/form-data';
    appShareInstance.defaults.headers['wizr-auth'] = localStorage.getItem('appshare_token');

    return appShareInstance.get(url);
  }

  private async requestInterceptor(config: any) {
    const apiKey = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY;
    if (apiKey) {
      const apiKeyValue = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY_VALUE;
      config.headers[apiKey] = apiKeyValue;
    }
    const token = await getValidToken();
    if (token) {
      config.headers['wizr-auth'] = token;
    }
    return config;
  }

  private formpostRequestInterceptor(config: any) {
    return config;
  }

  private responseInterceptor(response: any) {
    if (response?.data?.status === 401 || response?.status === 401) {
      dispatchSessionExpired();
    }
    if (response.data.status in errorCodes) {
      throw new Error(response.data.message);
    }
    return response;
  }

  private errorHandler(error: AxiosError) {
    const statuscode = error.response?.status;
    if (statuscode === 401) {
      dispatchSessionExpired();
    }
    if (statuscode !== undefined) {
      const newError: any = new Error(errorCodes[statuscode]);
      newError.response = error.response;
      newError.originalError = error;
      throw newError;
    } else {
      const newError: any = new Error('Unknown error occurred');
      newError.response = error.response;
      newError.originalError = error;
      throw newError;
    }
  }

  fcaAuthToken(): Promise<any> {
    const url = process.env.REACT_APP_FCA_AUTH_URL!;
    const headers = {
      'x-ibm-client-id': process.env.REACT_APP_FCA_X_CLIENT_ID!,
      'x-ibm-client-secret': process.env.REACT_APP_FCA_X_CLIENT_SECRET!,
      'Accept': 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    const data = qs.stringify({
      grant_type: process.env.REACT_APP_FCA_GRANT_TYPE!,
      client_id: process.env.REACT_APP_FCA_CLIENT_ID!,
      client_secret: process.env.REACT_APP_FCA_CLIENT_SECRET!,
      username: process.env.REACT_APP_FCA_USERNAME!,
      password: process.env.REACT_APP_FCA_PASSWORD!,
    });

    return axios.post(url, data, { headers });
  }

  createSpecifyingCase(url: string, body: any, token: string): Promise<any> {
    const instance = axios.create({
      timeout: this._instance.defaults.timeout,
    });

    instance.interceptors.response.use(this.responseInterceptor, this.errorHandler);
    instance.defaults.headers['x-ibm-client-id'] = process.env.REACT_APP_FCA_X_CLIENT_ID!;
    instance.defaults.headers['x-ibm-client-secret'] = process.env.REACT_APP_FCA_X_CLIENT_SECRET!;
    instance.defaults.headers['Content-Type'] = 'application/json';
    instance.defaults.headers['Authorization'] = `Bearer ${token}`;

    return instance.post(url, body);
  }

}

const WizrAPIInstance = new WizrAPIHandler();

export {
  WizrAPIInstance,
};
